// A member of staff's own feedback on one event (one each — a second go is an edit
// of the first), from the Feedback button on My events' past events: what they said,
// where it's got to, and — until the event is more than 30 days past, the same
// cut-off as giving it — a way to change it (PUT /feedback/mine/{id}).
//
// Changing it asks what changed. The server keeps that, with how each changed field
// read before, as a note on the record for the office (core/feedback.staff_edit_note),
// so correcting feedback never quietly rewrites what was first said.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { API_BASE_URL } from "../config";
import { FEEDBACK_NATURE_COLOR, FEEDBACK_STATUS_COLOR } from "../constants";
import { consentText, feedbackToForm, staffEditBody, staffEditProblems } from "../feedbackRules";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import FeedbackForm from "./FeedbackForm";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "");

function Submitted({ fb, onEdit }) {
  const consent = consentText(fb);
  return (
    <Stack spacing={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" sx={{ mr: 1 }}>
          {fb.reference} — {fmtDateTime(fb.submitted_at)}
        </Typography>
        {(fb.nature || []).map(n => (
          <Chip key={n} size="small" label={n} color={FEEDBACK_NATURE_COLOR[n] || "default"} />
        ))}
        <Chip size="small" variant="outlined" label={fb.status}
              color={FEEDBACK_STATUS_COLOR[fb.status] || "default"} />
        <Box sx={{ flex: 1 }} />
        {fb.editable && (
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        )}
      </Box>
      {fb.on_behalf_of && (
        <Typography variant="body2">
          On behalf of {fb.on_behalf_of}{consent ? ` — ${consent}` : ""}
        </Typography>
      )}
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{fb.details}</Typography>
      {!fb.editable && (
        <Typography variant="caption" color="text.secondary">
          Feedback can only be changed here for 30 days after the event. To add anything,
          email hello@shelter-stone.com quoting {fb.reference}.
        </Typography>
      )}
    </Stack>
  );
}

export default function MyFeedbackDialog({ event, feedback, person, onChanged, onClose }) {
  const fullScreen = useFullScreenDialog();
  const [editing, setEditing] = useState(null);   // the item being edited
  const [form, setForm] = useState(null);
  const [whatChanged, setWhatChanged] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const { busy, run } = useWriteLock();

  useEffect(() => { setEditing(null); }, [event]);
  if (!event || !feedback) return null;

  const startEdit = (fb) => {
    setEditing(fb);
    setForm(feedbackToForm(fb));
    setWhatChanged("");
    setShowErrors(false);
  };
  const problems = editing ? staffEditProblems(editing, form, whatChanged) : [];
  const save = () => {
    setShowErrors(true);
    if (problems.length) return;
    run(() => axios.put(`${API_BASE_URL}/feedback/mine/${editing.id}`,
                        staffEditBody(editing, form, whatChanged))
      .then(res => {
        notify(`Your changes to ${res.data.reference} were saved.`, "success");
        onChanged(res.data);
        setEditing(null);
      }));
  };

  const name = person ? [person.forename, person.surname].filter(Boolean).join(" ") : "";
  const contact = person ? [person.phone, person.email].filter(Boolean).join("  ·  ") : "";
  const reasonMissing = showErrors && !whatChanged.trim();

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        {editing ? `Change ${editing.reference}` : `Your feedback on ${event.label}`}
      </DialogTitle>
      <DialogContent dividers>
        {editing ? (
          <Stack spacing={3}>
            <FeedbackForm mode="staff" form={form} setForm={setForm} showErrors={showErrors}
                          fixed={{ name, contact, eventLabel: event.label,
                                   shiftDetails: editing.location_details }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>What changed?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A line on what you changed and why. It's kept with your feedback, along with what
                it said before, so the office can follow it.
              </Typography>
              <TextField label="What changed" required fullWidth multiline minRows={2}
                         value={whatChanged} onChange={(e) => setWhatChanged(e.target.value)}
                         error={reasonMissing}
                         helperText={reasonMissing ? "Please say what you changed" : " "} />
            </Box>
            {showErrors && problems.length > 0 && (
              <Alert severity="warning">{problems.join(" ")}</Alert>
            )}
          </Stack>
        ) : (
          <Submitted fb={feedback} onEdit={() => startEdit(feedback)} />
        )}
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
